<template>
    <div class="artist-tracks-page" ref="artisttrackview" @scroll.passive="listenBottomOut">
        <section class="artist-tracks-hero">
            <div class="artist-tracks-hero__copy">
                <div class="artist-tracks-hero__eyebrow">{{ $tt('艺术家') }}</div>
                <h1 class="artist-tracks-hero__title">{{ artist || $tt('未知艺术家') }}</h1>
                <p class="artist-tracks-hero__sub">
                    {{ $tt('在这里继续浏览这位艺术家的专辑和歌曲。') }}
                </p>
            </div>

            <div class="artist-tracks-hero__stats" aria-label="artist library summary">
                <div class="artist-tracks-hero__stat">
                    <span>{{ albums.length }}</span>
                    <small>{{ $tt('专辑') }}</small>
                </div>
                <div class="artist-tracks-hero__stat">
                    <span>{{ tracks.length }}</span>
                    <small>{{ $tt('歌曲') }}</small>
                </div>
            </div>
        </section>

        <nav class="artist-tracks-tabs" :aria-label="$tt('内容切换')">
            <button
                v-for="{ value, text } in options"
                :key="value"
                type="button"
                :class="['artist-tracks-tabs__item', section === value ? 'is-active' : '']"
                :aria-pressed="section === value"
                @click="handleChangeTab(value)"
            >
                <span>{{ $tt(text) }}</span>
                <strong>{{ value === 'albums' ? albums.length : tracks.length }}</strong>
            </button>
        </nav>

        <section class="artist-tracks-content">
            <div class="artist-tracks-content__header">
                <div>
                    <h2>{{ sectionTitle }}</h2>
                    <p>{{ sectionHint }}</p>
                </div>
                <div class="artist-tracks-content__count">
                    {{ activeCount }}
                </div>
            </div>

            <div v-if="section === 'tracks'" class="artist-tracks-content__body">
                <TrackList v-if="tracks.length" :tracks="tracks" :index-field="'index'" />
                <div v-else-if="!isActiveLoading" class="artist-tracks-empty">
                    <div class="artist-tracks-empty__mark"></div>
                    <h3>{{ $tt('暂无歌曲') }}</h3>
                    <p>{{ $tt('这位艺术家的歌曲还没有同步到库中。') }}</p>
                </div>
            </div>

            <div v-else class="artist-tracks-content__body">
                <AlbumList v-if="albums.length" :items="albums" />
                <div v-else-if="!isActiveLoading" class="artist-tracks-empty">
                    <div class="artist-tracks-empty__mark"></div>
                    <h3>{{ $tt('暂无专辑') }}</h3>
                    <p>{{ $tt('这位艺术家的专辑还没有同步到库中。') }}</p>
                </div>
            </div>

            <div class="artist-tracks-loading" v-if="isActiveLoading">
                <span class="loading loading-infinity loading-lg"></span>
            </div>

            <div class="artist-tracks-end" v-else-if="activeCount > 0 && !activeHasMore">
                {{ $tt('已加载全部内容') }}
            </div>
        </section>

        <button
            v-if="curScrollTop > 700"
            type="button"
            @click="scrollToTop"
            class="artist-tracks-top"
            :aria-label="$tt('回到顶部')"
        >
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 5.5 5.5 12l1.4 1.4 4.1-4.08V20h2V9.32l4.1 4.08 1.4-1.4L12 5.5z"></path>
            </svg>
        </button>
    </div>
</template>

<script>
import AlbumList from '@/views/library/album/AlbumList.vue'
import TrackList from '@/views/library/track/TrackList.vue'

export default {
    name: 'artist-tracks',
    components: {
        AlbumList,
        TrackList
    },
    data() {
        return {
            tracks: [],
            albums: [],
            id: this.$route.params.id,
            artist: this.$route.query.name,
            section: 'albums',
            loading: false,
            offset: 0,
            hasMore: true,
            loading2: false,
            offset2: 0,
            hasMore2: true,
            curScrollTop: 0,
            options: [
                { text: '专辑', value: 'albums' },
                { text: '歌曲', value: 'tracks' }
            ]
        }
    },
    computed: {
        activeCount() {
            return this.section === 'tracks' ? this.tracks.length : this.albums.length
        },
        activeHasMore() {
            return this.section === 'tracks' ? this.hasMore2 : this.hasMore
        },
        isActiveLoading() {
            return this.section === 'tracks' ? this.loading2 : this.loading
        },
        sectionTitle() {
            return this.section === 'tracks' ? this.$tt('歌曲') : this.$tt('专辑')
        },
        sectionHint() {
            if (this.section === 'tracks') {
                return this.$tt('按曲目顺序继续播放或管理。')
            }
            return this.$tt('从专辑封面开始探索这位艺术家。')
        }
    },
    async created() {
        await this.loadMore()
    },
    activated() {
        this.$nextTick(() => {
            if (this.$refs.artisttrackview) {
                this.$refs.artisttrackview.scrollTop = this.curScrollTop || 0
            }
        })
    },
    methods: {
        handleChangeTab(value) {
            if (this.section === value) return
            this.section = value
            if (this.activeCount === 0 && this.activeHasMore && !this.isActiveLoading) {
                this.loadMore()
            }
        },
        handleDelete() {
            this.$api.Task.deleteGenre({ id: this.id }).then((res) => {
                if (res.result) {
                    this.$myMsg.notify({
                        title: this.$tt('删除成功！'),
                        type: 'success',
                        time: 2000,
                    })
                    this.$router.push({ path: '/genres/a-z/' })
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('删除失败！'),
                        type: 'error',
                        time: 2000,
                    })
                }
            })
        },
        loadMore() {
            if (this.section === 'tracks') {
                if (this.loading2 || !this.hasMore2) return Promise.resolve()
                this.loading2 = true
                return this.$api.subsonic.getTracksByArtist2(this.id, 50, this.offset2).then(tracks => {
                    this.tracks.push(...tracks)
                    this.offset2 += tracks.length
                    this.hasMore2 = tracks.length > 0
                }).finally(() => {
                    this.loading2 = false
                })
            }

            if (this.loading || !this.hasMore) return Promise.resolve()
            this.loading = true
            return this.$api.subsonic.getAlbumsByArtist(this.id, 50, this.offset).then(albums => {
                this.albums.push(...albums)
                this.offset += albums.length
                this.hasMore = albums.length > 0
            }).finally(() => {
                this.loading = false
            })
        },
        listenBottomOut() {
            if (!this.$refs.artisttrackview) return
            const scroller = this.$refs.artisttrackview
            const scrollTop = scroller.scrollTop
            const scrollHeight = scroller.scrollHeight
            const clientHeight = scroller.clientHeight

            this.curScrollTop = scrollTop
            if (this.activeHasMore && !this.isActiveLoading && scrollTop + clientHeight >= scrollHeight - 80) {
                this.loadMore()
            }
        },
        scrollToTop() {
            if (!this.$refs.artisttrackview) return
            this.$refs.artisttrackview.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }
}
</script>

<style scoped>
.artist-tracks-page {
    --artist-text: oklch(var(--bc));
    --artist-muted: color-mix(in oklch, oklch(var(--bc)) 58%, transparent);
    --artist-soft-muted: color-mix(in oklch, oklch(var(--bc)) 42%, transparent);
    --artist-surface: color-mix(in oklch, oklch(var(--b1)) 74%, transparent);
    --artist-surface-strong: color-mix(in oklch, oklch(var(--b1)) 88%, transparent);
    --artist-surface-muted: color-mix(in oklch, oklch(var(--b2)) 82%, transparent);
    --artist-border: color-mix(in oklch, oklch(var(--bc)) 9%, transparent);
    --artist-accent: oklch(var(--n));
    --artist-accent-content: oklch(var(--nc));
    position: relative;
    height: calc(100dvh - 4rem);
    overflow-y: auto;
    box-sizing: border-box;
    padding: 1.15rem 1.25rem 5.5rem;
    color: var(--artist-text);
    background:
        radial-gradient(circle at 14% 0%, rgba(148, 163, 184, 0.18), transparent 28rem),
        radial-gradient(circle at 88% 8%, rgba(59, 130, 246, 0.12), transparent 24rem),
        linear-gradient(180deg, color-mix(in oklch, oklch(var(--b1)) 88%, oklch(var(--b2))), oklch(var(--b1)));
    -webkit-overflow-scrolling: touch;
}

.artist-tracks-hero {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 1rem;
    align-items: end;
    max-width: 1400px;
    margin: 0 auto;
    padding: 1.35rem;
    border-radius: 1.7rem;
    background:
        linear-gradient(135deg, var(--artist-surface-strong), var(--artist-surface)),
        oklch(var(--b1));
    border: 1px solid var(--artist-border);
    box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08);
    backdrop-filter: blur(18px);
}

.artist-tracks-hero__eyebrow {
    font-size: 0.82rem;
    font-weight: 800;
    color: var(--artist-soft-muted);
}

.artist-tracks-hero__title {
    max-width: 920px;
    margin-top: 0.32rem;
    font-size: clamp(2.1rem, 5vw, 4.6rem);
    line-height: 0.94;
    font-weight: 850;
    letter-spacing: -0.045em;
    color: var(--artist-text);
    word-break: break-word;
}

.artist-tracks-hero__sub {
    max-width: 32rem;
    margin-top: 0.9rem;
    color: var(--artist-muted);
    font-size: 0.98rem;
    line-height: 1.65;
}

.artist-tracks-hero__stats {
    display: grid;
    grid-template-columns: repeat(2, minmax(5.8rem, 1fr));
    gap: 0.75rem;
}

.artist-tracks-hero__stat {
    min-height: 5.8rem;
    border-radius: 1.25rem;
    padding: 0.95rem;
    background: var(--artist-surface-strong);
    box-shadow: 0 12px 28px rgba(148, 163, 184, 0.12);
}

.artist-tracks-hero__stat span,
.artist-tracks-content__count {
    display: block;
    font-variant-numeric: tabular-nums;
    font-weight: 850;
    letter-spacing: -0.04em;
    color: var(--artist-text);
}

.artist-tracks-hero__stat span {
    font-size: 2rem;
    line-height: 1;
}

.artist-tracks-hero__stat small {
    display: block;
    margin-top: 0.45rem;
    font-size: 0.78rem;
    font-weight: 700;
    color: var(--artist-muted);
}

.artist-tracks-tabs {
    display: inline-grid;
    grid-template-columns: repeat(2, minmax(7rem, 1fr));
    gap: 0.35rem;
    max-width: 1400px;
    margin: 1rem auto 0;
    padding: 0.35rem;
    border-radius: 999px;
    background: var(--artist-surface);
    box-shadow: 0 12px 28px rgba(148, 163, 184, 0.1);
    backdrop-filter: blur(14px);
}

.artist-tracks-tabs__item {
    min-height: 2.85rem;
    border: 0;
    border-radius: 999px;
    padding: 0 1.1rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    background: transparent;
    color: var(--artist-muted);
    font-weight: 800;
    white-space: nowrap;
    transition: transform 0.18s ease, background 0.18s ease, color 0.18s ease;
}

.artist-tracks-tabs__item:active,
.artist-tracks-top:active {
    transform: translateY(1px) scale(0.98);
}

.artist-tracks-tabs__item strong {
    min-width: 1.75rem;
    height: 1.35rem;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.45rem;
    background: color-mix(in oklch, oklch(var(--bc)) 10%, transparent);
    color: inherit;
    font-size: 0.75rem;
    font-variant-numeric: tabular-nums;
}

.artist-tracks-tabs__item.is-active {
    background: var(--artist-accent);
    color: var(--artist-accent-content);
    box-shadow: 0 16px 34px rgba(15, 23, 42, 0.16);
}

.artist-tracks-tabs__item.is-active strong {
    background: color-mix(in oklch, var(--artist-accent-content) 18%, transparent);
}

.artist-tracks-content {
    max-width: 1400px;
    margin: 1rem auto 0;
    padding: 1.1rem;
    border-radius: 1.7rem;
    background: var(--artist-surface);
    border: 1px solid var(--artist-border);
    box-shadow: 0 16px 34px rgba(148, 163, 184, 0.12);
    backdrop-filter: blur(18px);
}

.artist-tracks-content__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1rem;
}

.artist-tracks-content__header h2 {
    font-size: 1.35rem;
    line-height: 1.1;
    font-weight: 850;
    letter-spacing: -0.025em;
    color: var(--artist-text);
}

.artist-tracks-content__header p {
    margin-top: 0.3rem;
    color: var(--artist-muted);
    font-size: 0.92rem;
}

.artist-tracks-content__count {
    min-width: 3.25rem;
    height: 3.25rem;
    border-radius: 1rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--artist-surface-muted);
    font-size: 1.35rem;
    flex-shrink: 0;
}

.artist-tracks-content__body {
    min-height: 14rem;
}

.artist-tracks-loading,
.artist-tracks-end {
    display: flex;
    justify-content: center;
    padding: 1.2rem 0 0.35rem;
    color: var(--artist-soft-muted);
    font-size: 0.88rem;
    font-weight: 700;
}

.artist-tracks-empty {
    min-height: 18rem;
    border-radius: 1.35rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 2rem;
    background: var(--artist-surface-strong);
}

.artist-tracks-empty__mark {
    width: 3.25rem;
    height: 3.25rem;
    border-radius: 999px;
    margin-bottom: 0.9rem;
    background: var(--artist-accent);
    box-shadow: 0 16px 34px rgba(15, 23, 42, 0.14);
}

.artist-tracks-empty h3 {
    font-size: 1.05rem;
    font-weight: 800;
    color: var(--artist-text);
}

.artist-tracks-empty p {
    margin-top: 0.35rem;
    color: var(--artist-muted);
}

.artist-tracks-top {
    position: fixed;
    right: 2rem;
    bottom: 2rem;
    width: 3rem;
    height: 3rem;
    border: 0;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--artist-accent);
    color: var(--artist-accent-content);
    box-shadow: 0 20px 46px rgba(15, 23, 42, 0.2);
    transition: transform 0.18s ease, background 0.18s ease;
}

.artist-tracks-top:hover {
    background: color-mix(in oklch, var(--artist-accent) 86%, var(--artist-text));
}

.artist-tracks-top svg {
    width: 1.35rem;
    height: 1.35rem;
}

@media (prefers-reduced-motion: reduce) {
    .artist-tracks-tabs__item,
    .artist-tracks-top {
        transition: none;
    }

    .artist-tracks-page {
        scroll-behavior: auto;
    }
}

@media (max-width: 768px) {
    .artist-tracks-page {
        height: calc(100dvh - 12.25rem);
        padding: 0.75rem 0.35rem 1rem;
    }

    .artist-tracks-hero {
        grid-template-columns: 1fr;
        padding: 1rem;
        border-radius: 1.45rem;
    }

    .artist-tracks-hero__title {
        font-size: 2.05rem;
        line-height: 1;
    }

    .artist-tracks-hero__stats {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .artist-tracks-tabs {
        display: grid;
        width: 100%;
    }

    .artist-tracks-content {
        padding: 0.75rem;
        border-radius: 1.45rem;
    }

    .artist-tracks-content__header {
        align-items: center;
    }

    .artist-tracks-content__header p {
        display: none;
    }

    .artist-tracks-top {
        right: 1rem;
        bottom: 7rem;
    }
}
</style>
